/**
 * Клиент API.
 *
 * Access-токен живёт 15 минут, поэтому клиент сам обновляет его по refresh-токену
 * при первом же 401 и повторяет запрос. Если обновление не удалось, сессия
 * сбрасывается, и приложение показывает вход заново.
 */

import type {
  AdminStatsDto,
  AdminUserDto,
  AdminWithdrawalDto,
  WithdrawalOptionDto,
  WithdrawalRequestDto,
  WithdrawalKind,
  AuthResponse,
  CaseCollectionDto,
  CaseDto,
  CaseOpeningDto,
  DepositDto,
  FairnessDto,
  InventoryItemDto,
  PaymentMethodDto,
  PlinkoDropDto,
  ChatRoomDto,
  SlotSpinDto,
  SlotSymbolDto,
  UserDto,
  PaidPlan,
} from "@koshkodrop/shared";

const ACCESS_KEY = "koshkodrop.access";
const REFRESH_KEY = "koshkodrop.refresh";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: { channel?: string; channelUrl?: string },
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const tokens = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  save(access: string, refresh?: string): void {
    localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  /** Не пытаться обновлять токен: используется самим обновлением. */
  skipRefresh?: boolean;
}

async function rawRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";

  const access = tokens.access;
  if (access) headers.authorization = `Bearer ${access}`;

  const response = await fetch(`/api${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    let code = "http_error";
    let message = `Запрос завершился с кодом ${response.status}`;
    try {
      const payload = (await response.json()) as {
        error?: string;
        message?: string;
        channel?: string;
        channelUrl?: string;
      };
      code = payload.error ?? code;
      message = payload.message ?? message;
      throw new ApiError(response.status, code, message, {
        channel: payload.channel,
        channelUrl: payload.channelUrl,
      });
    } catch (error) {
      if (error instanceof ApiError) throw error;
      // Тело может быть пустым: оставляем сообщение по умолчанию
    }
    throw new ApiError(response.status, code, message);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Обновление токена выполняется не чаще одного раза за момент времени. */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refresh = tokens.refresh;
    if (!refresh) return false;

    try {
      const result = await rawRequest<{ accessToken: string; refreshToken: string }>(
        "/auth/refresh",
        { method: "POST", body: { refreshToken: refresh }, skipRefresh: true },
      );
      tokens.save(result.accessToken, result.refreshToken);
      return true;
    } catch {
      tokens.clear();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await rawRequest<T>(path, options);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401 && !options.skipRefresh) {
      const refreshed = await refreshSession();
      if (refreshed) return rawRequest<T>(path, options);
    }
    throw error;
  }
}

export const api = {
  loginWithTelegram(initData: string, recheck = false) {
    return request<AuthResponse>("/auth/telegram", {
      method: "POST",
      body: { initData, recheck },
      skipRefresh: true,
    });
  },

  /**
   * Вход для разработки. Маршрут существует только вне production, поэтому
   * вызывать его имеет смысл лишь из dev-сборки мини-аппа.
   */
  loginAsDev(balance: string) {
    return request<AuthResponse>("/auth/dev", {
      method: "POST",
      body: { telegramId: 1, balance },
      skipRefresh: true,
    });
  },

  me() {
    return request<{ user: UserDto; clientSeed: string }>("/me");
  },

  buyPlan(plan: PaidPlan) {
    return request<{ user: UserDto; balance: string }>("/me/plan", {
      method: "POST",
      body: { plan },
    });
  },

  updateCosmetics(body: { nickColor?: string | null; nickSticker?: string | null }) {
    return request<{ user: UserDto }>("/me/cosmetics", {
      method: "PATCH",
      body,
    });
  },

  myBets() {
    return request<{
      bets: {
        id: string;
        roundSequence: number;
        stake: string;
        cashedOutAt: number | null;
        payout: string | null;
        status: string;
        crashPoint: number | null;
        createdAt: string;
      }[];
    }>("/me/bets");
  },

  transactions() {
    return request<{
      transactions: {
        id: string;
        kind: string;
        amount: string;
        balanceAfter: string | null;
        createdAt: string;
      }[];
    }>("/me/transactions");
  },

  paymentMethods() {
    return request<{
      methods: PaymentMethodDto[];
      minAmountPaws: string;
      maxAmountPaws: string;
    }>("/payments/methods");
  },

  createDeposit(provider: string, amountPaws: string) {
    return request<{
      depositId: string;
      amountPaws: string;
      instructions: { payUrl?: string; providerRef: string | null; expiresInSec?: number };
    }>("/payments/deposits", { method: "POST", body: { provider, amountPaws } });
  },

  getDeposit(id: string) {
    return request<{ deposit: DepositDto }>(`/payments/deposits/${id}`);
  },

  listCases() {
    return request<{ collections: CaseCollectionDto[]; cases: CaseDto[] }>("/cases");
  },

  listCaseOpenings(limit = 12) {
    return request<{ openings: CaseOpeningDto[] }>(`/cases/history?limit=${limit}`);
  },

  openCase(slug: string, count = 1) {
    return request<{ opening: CaseOpeningDto; openings: CaseOpeningDto[]; balance: string }>(
      `/cases/${slug}/open`,
      {
        method: "POST",
        body: { count },
      },
    );
  },

  spinSlots(stake: string) {
    return request<{ spin: SlotSpinDto; balance: string }>("/slots/spin", {
      method: "POST",
      body: { stake },
    });
  },

  dropPlinko(stake: string, count = 1) {
    return request<{ drop: PlinkoDropDto; drops: PlinkoDropDto[]; balance: string }>("/plinko/drop", {
      method: "POST",
      body: { stake, count },
    });
  },

  listInventory() {
    return request<{ items: InventoryItemDto[] }>("/inventory");
  },

  /** Активация обращает предмет в лапки, отказ просто закрывает его. */
  resolveInventoryItem(id: string, action: "activate" | "decline") {
    return request<{ item: InventoryItemDto; balance: string }>(`/inventory/${id}/${action}`, {
      method: "POST",
    });
  },

  fairness() {
    return request<FairnessDto>("/fairness");
  },

  rotateSeed(clientSeed: string) {
    return request<FairnessDto>("/fairness/rotate", { method: "POST", body: { clientSeed } });
  },

  slotCatalog() {
    return request<{ symbols: SlotSymbolDto[]; minBet: string; maxBet: string }>("/slots");
  },

  verifyRound(sequence: number) {
    return request<{
      sequence: number;
      commitHash: string;
      serverSeed: string | null;
      crashPoint: number | null;
      houseEdgeBp: number;
    }>(`/crash/rounds/${sequence}`);
  },

  adminStats() {
    return request<{ stats: AdminStatsDto }>("/admin/stats");
  },

  adminListUsers(params?: { q?: string; offset?: number; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.q?.trim()) search.set("q", params.q.trim());
    if (params?.offset !== undefined) search.set("offset", String(params.offset));
    if (params?.limit !== undefined) search.set("limit", String(params.limit));
    const query = search.toString();
    return request<{
      users: AdminUserDto[];
      total: number;
      offset: number;
      limit: number;
    }>(`/admin/users${query ? `?${query}` : ""}`);
  },

  adminGetUser(userId: string) {
    return request<{ user: AdminUserDto }>(`/admin/users/${userId}`);
  },

  adminSetStatus(userId: string, status: "active" | "blocked") {
    return request<{ user: AdminUserDto }>(`/admin/users/${userId}/status`, {
      method: "PATCH",
      body: { status },
    });
  },

  adminAdjustBalance(userId: string, amount: string, reason?: string) {
    return request<{ user: AdminUserDto; balanceAfter: string }>(
      `/admin/users/${userId}/adjust-balance`,
      { method: "POST", body: { amount, reason } },
    );
  },

  withdrawalOptions() {
    return request<{ options: WithdrawalOptionDto[] }>("/withdrawals/options");
  },

  withdrawals() {
    return request<{ requests: WithdrawalRequestDto[] }>("/withdrawals");
  },

  createWithdrawal(body: {
    kind: WithdrawalKind;
    amountPaws?: string;
    contactUsername?: string;
  }) {
    return request<{ request: WithdrawalRequestDto; balance: string }>("/withdrawals", {
      method: "POST",
      body,
    });
  },

  adminListWithdrawals(params?: { status?: string; offset?: number; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.status) search.set("status", params.status);
    if (params?.offset !== undefined) search.set("offset", String(params.offset));
    if (params?.limit !== undefined) search.set("limit", String(params.limit));
    const query = search.toString();
    return request<{
      requests: AdminWithdrawalDto[];
      total: number;
      offset: number;
      limit: number;
    }>(`/admin/withdrawals${query ? `?${query}` : ""}`);
  },

  adminCompleteWithdrawal(requestId: string) {
    return request<{ request: AdminWithdrawalDto }>(`/admin/withdrawals/${requestId}/complete`, {
      method: "POST",
    });
  },

  adminRefundWithdrawal(requestId: string) {
    return request<{ request: AdminWithdrawalDto }>(`/admin/withdrawals/${requestId}/refund`, {
      method: "POST",
    });
  },

  adminRetryWithdrawal(requestId: string) {
    return request<{ request: AdminWithdrawalDto }>(`/admin/withdrawals/${requestId}/retry`, {
      method: "POST",
    });
  },

  chatRooms() {
    return request<{ rooms: ChatRoomDto[] }>("/chat/rooms");
  },
};
